/**
 * Errores en lenguaje claro: qué ha pasado y qué hacer.
 *
 * Lógica pura y sin dependencias de plataforma. La web y la app móvil
 * convierten primero su error (`ApiClientError`, `NetworkError`…) en un
 * {@link ErrorSource} y de aquí sale el texto que ve la persona, el mismo en
 * las dos. Ramifica sobre el `code` estable del contrato, nunca sobre el
 * `message` del servidor, que está en inglés y puede cambiar.
 */
// --- Textos -----------------------------------------------------------------
export const ERROR_COPY = {
    offlineQueued: 'No hay conexión. Lo que apuntes se guarda en el móvil y se subirá solo.',
    offline: 'No hay conexión con el servidor. Revisa la conexión y vuelve a intentarlo.',
    offlineSaving: 'No hay conexión y no se ha guardado. Lo que has escrito sigue aquí: revisa la conexión y vuelve a intentarlo.',
    server: 'Algo ha fallado en el servidor. Vuelve a intentarlo en un momento.',
    serverSaving: 'Algo ha fallado en el servidor y no se ha guardado. Lo que has escrito sigue aquí: vuelve a intentarlo en un momento.',
    invalidCredentials: 'El correo o la contraseña no son correctos.',
    sessionExpired: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
    validation: 'Hay algún dato que no es válido.',
    validationNoFields: 'Hay algún dato que no es válido. Revísalo y vuelve a intentarlo.',
    forbidden: 'No tienes permiso para hacer esto.',
    notFound: 'No lo encontramos: puede que se haya borrado. Vuelve atrás y recarga la página.',
    conflict: 'No se ha podido hacer porque choca con otro cambio. Recarga y vuelve a intentarlo.',
    tooLarge: 'La imagen pesa demasiado: el máximo son 5 MB. Prueba con una más pequeña.',
    unknown: 'Ha pasado algo inesperado. Vuelve a intentarlo y, si se repite, recarga la app.',
};
/** Errores del contrato con un texto propio, más concreto que el de su familia. */
const CODE_COPY = {
    wrong_password: { kind: 'forbidden', message: 'La contraseña actual no es correcta.' },
    blocked: {
        kind: 'forbidden',
        message: 'No puedes interactuar con esta cuenta porque hay un bloqueo entre las dos.',
    },
    invite_required: {
        kind: 'validation',
        message: 'Para crear una cuenta necesitas el código de invitación que te han pasado.',
    },
    email_taken: {
        kind: 'conflict',
        message: 'Ya existe una cuenta con este correo. Si es tuya, inicia sesión.',
    },
    handle_taken: {
        kind: 'conflict',
        message: 'Ese nombre de usuario ya lo usa otra persona. Prueba con otro.',
    },
    exercise_in_use: {
        kind: 'conflict',
        message: 'No se puede borrar: el ejercicio está en alguna rutina o entreno. Quítalo de ahí antes.',
    },
    food_in_use: {
        kind: 'conflict',
        message: 'No se puede borrar: el alimento es ingrediente de alguna receta tuya.',
    },
    not_refreshable: {
        kind: 'conflict',
        message: 'Este alimento lo creaste tú: no hay ninguna fuente externa de la que actualizarlo.',
    },
    already_reported: { kind: 'conflict', message: 'Ya lo habías denunciado. Lo revisaremos.' },
    upstream_unavailable: {
        kind: 'server',
        message: 'La base de datos de alimentos externa no responde. Vuelve a intentarlo en un momento o apúntalo a mano.',
    },
    // Iniciar sesión con Google o Apple.
    password_not_set: {
        kind: 'forbidden',
        message: 'Tu cuenta entra con Google o Apple y aún no tiene contraseña. Ponle una en Ajustes → Cuenta para poder hacer esto.',
    },
    reauth_required: {
        kind: 'forbidden',
        message: 'Para poner la contraseña, confirma antes que eres tú entrando otra vez con tu proveedor.',
    },
    social_session_expired: {
        kind: 'session_expired',
        message: 'El inicio de sesión con el proveedor ha caducado o ya se usó. Vuelve a empezar desde el botón.',
    },
    provider_already_linked: {
        kind: 'conflict',
        message: 'Ya tienes una cuenta de ese proveedor vinculada. Desvincúlala antes si quieres usar otra.',
    },
    identity_in_use: {
        kind: 'conflict',
        message: 'Esa cuenta de Google o Apple ya está vinculada a otro usuario de la app.',
    },
    last_login_method: {
        kind: 'conflict',
        message: 'Es la única forma que tienes de entrar en tu cuenta. Ponle antes una contraseña para poder quitarla.',
    },
};
/** Campo del formulario al que pertenece cada conflicto de registro. */
const CODE_FIELD = {
    email_taken: { field: 'email', message: 'Ya existe una cuenta con este correo.' },
    handle_taken: { field: 'handle', message: 'Este nombre de usuario ya está cogido.' },
    invite_required: { field: 'inviteCode', message: 'Necesitas un código de invitación válido.' },
    wrong_password: { field: 'currentPassword', message: 'La contraseña actual no es correcta.' },
};
// --- Esperas ------------------------------------------------------------------
/** «45 segundos», «1 minuto», «3 minutos»: siempre redondeando hacia arriba. */
export function formatWait(seconds) {
    const safe = Math.max(1, Math.ceil(seconds));
    if (safe < 60)
        return `${safe} ${safe === 1 ? 'segundo' : 'segundos'}`;
    const minutes = Math.ceil(safe / 60);
    return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
}
export function rateLimitedMessage(retryAfterSeconds) {
    if (retryAfterSeconds === null || retryAfterSeconds === undefined) {
        return 'Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.';
    }
    return `Demasiados intentos seguidos. Espera ${formatWait(retryAfterSeconds)} y vuelve a probar.`;
}
// --- Validación por campo ---------------------------------------------------
/** Traducciones exactas de los motivos de validación del backend. */
const FIELD_EXACT = {
    'is required': 'Es obligatorio.',
    'must match ^[a-z0-9_]{3,30}$': 'Usa de 3 a 30 caracteres: minúsculas, números o guion bajo.',
    'must be an IANA time zone name': 'La zona horaria no es válida.',
    'must be 8 to 14 digits': 'Debe tener entre 8 y 14 dígitos.',
    'must not be earlier than from': 'No puede ser anterior a la fecha de inicio.',
    'must not be negative': 'No puede ser negativo.',
    'must be a positive integer': 'Debe ser un número entero positivo.',
    'must be a multiple of 0.5': 'Debe ser múltiplo de 0,5.',
};
const FIELD_PREFIXES = [
    ['must be between ', 'Debe estar entre '],
    ['must be at most ', 'Debe tener como máximo '],
    ['must be at least ', 'Debe tener al menos '],
    ['must hold at most ', 'Debe tener como máximo '],
    ['must hold between ', 'Debe tener entre '],
    ['must be one of ', 'Debe ser uno de estos valores: '],
    ['must be greater than 0 and at most ', 'Debe ser mayor que 0 y como máximo '],
];
const WORDS = [
    [/ and /g, ' y '],
    [/ characters?( long)?/g, ' caracteres'],
    [/ items/g, ' elementos'],
    [/ days/g, ' días'],
    [/ exercises/g, ' ejercicios'],
];
function sentence(text) {
    const trimmed = text.trim();
    if (trimmed === '')
        return trimmed;
    const capital = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}
/**
 * Pasa a español el motivo de validación de un campo. Los motivos del backend
 * están en inglés («must be between 1 and 60 characters»); los que ya vienen
 * en español se dejan como están.
 */
export function translateFieldMessage(message) {
    const trimmed = message.trim();
    const exact = FIELD_EXACT[trimmed];
    if (exact !== undefined)
        return exact;
    for (const [prefix, translated] of FIELD_PREFIXES) {
        if (trimmed.startsWith(prefix)) {
            let rest = trimmed.slice(prefix.length);
            // Los valores de un enumerado son claves de la API: se dejan tal cual.
            if (!prefix.endsWith('one of ')) {
                for (const [pattern, replacement] of WORDS)
                    rest = rest.replace(pattern, replacement);
            }
            return sentence(translated + rest);
        }
    }
    return sentence(trimmed);
}
/** Detalle campo a campo de un `validation_error`, ya traducido. */
function validationFields(details) {
    const fields = {};
    if (details === undefined)
        return fields;
    for (const [field, value] of Object.entries(details)) {
        // `reason` y `missing` son el detalle de «perfil incompleto», no campos.
        if (field === 'reason' || field === 'missing')
            continue;
        if (typeof value === 'string' && value.trim() !== '') {
            fields[field] = translateFieldMessage(value);
        }
    }
    return fields;
}
/** Nombre legible de los campos más comunes, para el aviso general. */
const FIELD_LABELS = {
    email: 'Correo',
    password: 'Contraseña',
    currentPassword: 'Contraseña actual',
    newPassword: 'Contraseña nueva',
    displayName: 'Nombre',
    name: 'Nombre',
    handle: 'Nombre de usuario',
    inviteCode: 'Código de invitación',
    bio: 'Biografía',
    weightKg: 'Peso',
    heightCm: 'Altura',
    bodyFatPct: 'Grasa corporal',
    birthDate: 'Fecha de nacimiento',
    note: 'Nota',
    text: 'Texto',
    servings: 'Raciones',
    servingSize: 'Tamaño de la ración',
    barcode: 'Código de barras',
};
/**
 * Aviso general de un `validation_error`. Lo pinta quien no puede poner cada
 * mensaje junto a su campo; quien sí puede, usa `fieldErrors` y no lo enseña.
 */
function validationMessage(fields) {
    const entries = Object.entries(fields);
    if (entries.length === 0)
        return ERROR_COPY.validationNoFields;
    const detail = entries
        .map(([field, message]) => {
        const label = FIELD_LABELS[field];
        return label === undefined
            ? message
            : `${label}: ${message.charAt(0).toLowerCase()}${message.slice(1)}`;
    })
        .join(' ');
    return `${ERROR_COPY.validation} ${detail}`;
}
// --- Mapeo ------------------------------------------------------------------
function result(kind, message, canRetry, fieldErrors = {}, retryAfterSeconds = null) {
    return { kind, message, canRetry, fieldErrors, retryAfterSeconds };
}
/** Convierte un error en un texto claro y en lo que se puede hacer con él. */
export function friendlyError(source, options = {}) {
    if (source.type === 'network') {
        if (options.queued === true)
            return result('offline', ERROR_COPY.offlineQueued, false);
        return result('offline', options.saving === true ? ERROR_COPY.offlineSaving : ERROR_COPY.offline, true);
    }
    if (source.type === 'unknown')
        return result('unknown', ERROR_COPY.unknown, true);
    const { status, code } = source;
    if (code === 'rate_limited' || status === 429) {
        const wait = source.retryAfterSeconds ?? null;
        return result('rate_limited', rateLimitedMessage(wait), false, {}, wait);
    }
    if (code === 'invalid_credentials') {
        return result('invalid_credentials', ERROR_COPY.invalidCredentials, false);
    }
    if (code === 'unauthorized' || code === 'invalid_refresh_token') {
        return result('session_expired', ERROR_COPY.sessionExpired, false);
    }
    const fieldErrors = {};
    const field = CODE_FIELD[code];
    if (field !== undefined)
        fieldErrors[field.field] = field.message;
    const specific = CODE_COPY[code];
    if (specific !== undefined) {
        // Un fallo de una fuente externa sí se arregla esperando un poco.
        const canRetry = specific.kind === 'server';
        return result(specific.kind, specific.message, canRetry, fieldErrors);
    }
    if (code === 'validation_error' || status === 400 || status === 422) {
        const fields = validationFields(source.details);
        return result('validation', validationMessage(fields), false, fields);
    }
    if (code === 'payload_too_large' || status === 413) {
        return result('too_large', ERROR_COPY.tooLarge, false);
    }
    if (code === 'forbidden' || status === 403)
        return result('forbidden', ERROR_COPY.forbidden, false);
    if (code === 'not_found' || status === 404)
        return result('not_found', ERROR_COPY.notFound, false);
    if (status === 409)
        return result('conflict', ERROR_COPY.conflict, false);
    // 5xx, `internal` y cualquier estado que no sepamos explicar: el problema es
    // del servidor, no de lo que se ha enviado, así que se puede reintentar.
    if (status >= 500 || code === 'internal') {
        return result('server', options.saving === true ? ERROR_COPY.serverSaving : ERROR_COPY.server, true);
    }
    return result('unknown', ERROR_COPY.unknown, true);
}
// --- Pista tras varios fallos de login --------------------------------------
/** A partir de cuántos fallos seguidos de contraseña se da la pista. */
export const LOGIN_HINT_AFTER_FAILURES = 3;
export const LOGIN_HINT = {
    title: '¿Sigues sin poder entrar?',
    tips: [
        'Revisa las mayúsculas (el bloqueo de mayúsculas) y que no sobren espacios al principio o al final.',
        '¿Te registraste con otro correo? Prueba con el que usaste al crear la cuenta.',
        'Si aun así no entras, pide ayuda a quien te invitó a la app: puede revisar tu cuenta.',
    ],
};
/**
 * Cuenta los fallos de contraseña seguidos. Sólo suma un `invalid_credentials`:
 * un corte de red o un 429 no dicen nada de si la contraseña era buena, así
 * que dejan la cuenta como estaba.
 */
export function nextLoginFailureCount(previous, source) {
    if (source.type === 'api' && source.code === 'invalid_credentials')
        return previous + 1;
    return previous;
}
/** `true` cuando ya toca enseñar la pista de {@link LOGIN_HINT}. */
export function shouldShowLoginHint(failures) {
    return failures >= LOGIN_HINT_AFTER_FAILURES;
}
//# sourceMappingURL=friendlyError.js.map